/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `ChatHeader` for the Caintra Connect frontend.
 * @module components/Chat/ChatHeader
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { ReactComponent as OnlineIcon } from "../../assets/icons/Chat/OnlineIcon.svg";
import { ReactComponent as OfflineIcon } from "../../assets/icons/Chat/OfflineIcon.svg";
import { ReactComponent as BackArrowIcon } from "../../assets/icons/Chat/BackArrowIcon.svg";
import { ReactComponent as MoreIcon } from "../../assets/icons/Chat/MoreIcon.svg";
import Caintra_Logo_03 from "../../assets/Caintra_Logo_03.png";
import { getCompanySizeOptions } from "../../data/selectOptions";
import { getSelectedOption } from "../../utils/common";
import { isUserOnline } from "../../logic/chat";

const ChatHeader = ({
  t,
  styles,
  selectedUser,
  selectedCompany,
  activeConnections,
  isReconnecting,
  isUserActive,
  headerTitleOverride,
  hideUserEmail,
  showDropdown,
  onBackToInbox,
  onToggleDropdown,
  onReassignChat,
  onReportUser,
}) => {
  const userIsOnline = isUserOnline(selectedUser._id, activeConnections);

  return (
    <div className={styles.chatHeader}>
      {onBackToInbox && (
        <button className={styles.chatBackButton} onClick={onBackToInbox}>
          <BackArrowIcon className={styles.chatBackIcon} />
          <span>{t("pages.chat.back")}</span>
        </button>
      )}
      <div className={styles.chatHeaderAvatar}>
        <img
          className={styles.avatar}
          src={Caintra_Logo_03}
          alt={t("common.avatarAlt")}
        />
      </div>
      <div className={styles.chatHeaderInfoContainer}>
        <span className={styles.chatHeaderTitle}>
          {headerTitleOverride ||
            selectedCompany?.nombreComercial ||
            selectedUser.empresa}
        </span>
        {selectedCompany && (
          <div className={styles.companyInfo}>
            <span>
              Empresa{" "}
              {getSelectedOption(
                getCompanySizeOptions(t),
                selectedCompany.tamanoDeLaEmpresa,
              )?.label || selectedCompany.tamanoDeLaEmpresa}
            </span>
          </div>
        )}
        <div className={styles.chatHeaderInfo}>
          <div className={styles.onlineStatus}>
            {userIsOnline ? (
              <OnlineIcon className={styles.onlineIcon} />
            ) : (
              <OfflineIcon className={styles.onlineIcon} />
            )}
            <span className={styles.chatHeaderStatus}>
              {userIsOnline
                ? t("pages.chat.online")
                : t("pages.chat.offline")}
            </span>
            <span className={styles.userInfo}>
              {selectedUser.nombre}
              {!hideUserEmail && ` (${selectedUser.correoElectronico})`}
            </span>
          </div>
          <div className={styles.connectionStatus}>
            {isReconnecting && (
              <span className={styles.reconnectingText}>
                {t("pages.chat.reconnecting")}
              </span>
            )}
            {!isUserActive && (
              <span className={styles.inactiveText}>
                {t("pages.chat.inactive")}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className={styles.chatHeaderDropdownContainer}>
        <button
          className={styles.chatHeaderDropdownButton}
          onClick={onToggleDropdown}
        >
          <MoreIcon />
        </button>
        {showDropdown && (
          <div className={styles.chatHeaderDropdownMenu}>
            <button
              className={styles.chatHeaderDropdownItem}
              onClick={onReassignChat}
            >
              {t("pages.chat.reassignChat")}
            </button>
            <div className={styles.chatHeaderDropdownDivider}></div>
            <button
              className={`${styles.chatHeaderDropdownItem} ${styles.chatHeaderRejectItem}`}
              onClick={onReportUser}
            >
              {t("pages.chat.reportUser")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatHeader;
